/**
 * Notification Center API — ARIA inbox (PR4).
 */
import { apiFetch } from './client'

export type NotificationTier = 'action_required' | 'money' | 'deadline' | 'fyi'

export interface AppNotification {
  id: number
  event_type: string
  tier: NotificationTier
  title: string
  body: string | null
  deep_link: string | null
  entity_type: string | null
  entity_id: number | null
  read_at: string | null
  created_at: string
  group_key: string | null
  snoozed_until?: string | null
}

export interface NotificationPreferences {
  action_required: { in_app: boolean; push: boolean; email: string }
  money: { in_app: boolean; push: boolean; email: string }
  deadline: { in_app: boolean; push: boolean; email: string }
  fyi: { in_app: boolean; push: boolean; email: string }
  sub_upload_email?: boolean
  snoozed_until: string | null
}

export async function fetchNotifications(): Promise<AppNotification[]> {
  const res = await apiFetch<{ data: AppNotification[] }>('GET', '/api/notifications')
  return res.data ?? []
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await apiFetch<{ data: { count: number } }>('GET', '/api/notifications/unread-count')
  return res.data?.count ?? 0
}

export async function fetchPendingSubUploadCount(): Promise<number> {
  const res = await apiFetch<{ data: { count: number } }>('GET', '/api/dashboard/pending-sub-uploads')
  return res.data?.count ?? 0
}

export async function markNotificationRead(id: number): Promise<void> {
  await apiFetch('POST', `/api/notifications/${id}/read`)
}

export async function snoozeNotification(id: number, days = 2): Promise<void> {
  await apiFetch('POST', `/api/notifications/${id}/snooze`, { days })
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiFetch('POST', '/api/notifications/read-all')
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const res = await apiFetch<{ data: NotificationPreferences }>('GET', '/api/notifications/preferences')
  return res.data
}

export async function saveNotificationPreferences(
  patch: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  const res = await apiFetch<{ data: NotificationPreferences }>(
    'PATCH',
    '/api/notifications/preferences',
    patch,
  )
  return res.data
}

export async function fetchVapidPublicKey(): Promise<{ publicKey: string; enabled: boolean }> {
  const res = await apiFetch<{ data: { publicKey: string; enabled: boolean } }>(
    'GET',
    '/api/notifications/push/vapid-public-key',
  )
  return res.data
}

export async function subscribePush(subscription: PushSubscriptionJSON): Promise<void> {
  await apiFetch('POST', '/api/notifications/push/subscribe', { subscription })
}

export async function publishNotification(body: {
  event_type: string
  tier: NotificationTier
  title: string
  body?: string
  deep_link?: string
  entity_type?: string
  entity_id?: number
  group_key?: string
}): Promise<AppNotification> {
  const res = await apiFetch<{ data: AppNotification }>('POST', '/api/notifications/publish', body)
  return res.data
}

/** Group fyi rows with same group_key for inbox display. */
export function groupNotifications(items: AppNotification[]) {
  const seen = new Set<string>()
  const out: Array<AppNotification | { group: true; key: string; title: string; items: AppNotification[] }> = []

  for (const n of items) {
    if (n.tier === 'fyi' && n.group_key) {
      if (seen.has(n.group_key)) continue
      const siblings = items.filter((x) => x.group_key === n.group_key)
      seen.add(n.group_key)
      if (siblings.length >= 2) {
        const vendor = n.title.split(' uploaded')[0] || 'Updates'
        out.push({
          group: true,
          key: n.group_key,
          title: `${siblings.length} updates from ${vendor}`,
          items: siblings,
        })
      } else {
        out.push(n)
      }
    } else {
      out.push(n)
    }
  }
  return out
}
